import DoneAllIcon from "@mui/icons-material/DoneAll";
import TodayIcon from "@mui/icons-material/Today";
import { Box, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { AcceptanceButton } from "../../components/Notifications/AcceptanceButton/AcceptanceButton";
import NotificationDate from "../../components/Notifications/NotificationDate";
import NotificationNotFound from "../../components/Notifications/NotificationNotFound";
import PagesContainer from "../../components/UI/PagesContainer";
import useFetch from "../../hooks/useFetch";
import { INotification } from "../../models/notifications-user";
import { getNotification } from "../../services/notifications.service";
import { useNotificationsUser } from "../../store/contexts/NotificationsUserContext";
import { useAppSelector } from "../../store/store";

const translationPath = "page.notification.";

const Notification = () => {
  const { t } = useTranslation();

  const notificationPrivileges = useAppSelector((state) =>
    state.privilege.privileges.filter((p) => p.domainName === "notification")
  );
  const hasUpdatePermission = !!notificationPrivileges.find((p) =>
    p.actions.some((a) => a === "Update")
  );

  const { id } = useParams();

  const actualLang = useAppSelector((state) => state.language.languageId);

  const [notification, setNotification] = useState<INotification>();

  const [showAcceptanceButton, setShowAcceptanceButton] = useState(false);

  const { sendRequest, loading } = useFetch(getNotification);

  const { markNotificationAsReadHandler, markUnreadNotificationAsReadHandler } =
    useNotificationsUser();

  const getNotificationHandler = useCallback(async () => {
    if (id && actualLang) {
      const { data, success } = await sendRequest({
        id,
        languageId: actualLang,
      });
      if (data && success) {
        setNotification(data);
        if (data.notificationBatch.type.code === "3") {
          setShowAcceptanceButton(true);
        }
      }
    }
  }, [sendRequest, id, actualLang]);

  useEffect(() => {
    getNotificationHandler();
  }, [getNotificationHandler]);

  useEffect(() => {
    if (!id) return;
    if (notification && !notification.isRead && hasUpdatePermission) {
      markNotificationAsReadHandler(id);
      markUnreadNotificationAsReadHandler(id, true);
    }
  }, [
    id,
    notification,
    markNotificationAsReadHandler,
    markUnreadNotificationAsReadHandler,
    hasUpdatePermission,
  ]);

  return (
    <PagesContainer
      title={
        notification
          ? notification.notificationBatchUserContent.title
          : t(translationPath + "title")
      }
      loading={loading}
      redirectTo={"/notifications"}
    >
      <Box height={"calc(100vh - 14rem)"}>
        {!notification && <NotificationNotFound />}
        {notification && (
          <>
            <Box pb={2} display={"flex"} alignItems={"center"} gap={1}>
              {notification.created && (
                <NotificationDate
                  date={notification.created}
                  icon={<TodayIcon fontSize="small" />}
                  tooltipText={t(translationPath + "created")}
                />
              )}
              {notification.readDate && (
                <NotificationDate
                  date={notification.readDate}
                  icon={<DoneAllIcon fontSize="small" />}
                  tooltipText={t(translationPath + "read")}
                />
              )}
            </Box>
            <Typography>
              <div
                dangerouslySetInnerHTML={{
                  __html: notification?.notificationBatchUserContent.content,
                }}
              />
            </Typography>
            {showAcceptanceButton && !notification.acceptanceDate && (
              <AcceptanceButton
                onHideAcceptanceButton={setShowAcceptanceButton}
              />
            )}
          </>
        )}
      </Box>
    </PagesContainer>
  );
};

export default Notification;
